/**
 * LegalOnboardingWizard — parcours 4 étapes pour un nouveau tenant.
 *   1. Profil société (dénomination · forme · capital · SIRET · siège · clôture)
 *   2. Dirigeant principal (nom · fonction · date_nomination)
 *   3. Associé principal (nom · parts · pourcentage · adresse)
 *   4. Upload KBIS (avec OCR auto-prefill si dispo)
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputFR } from "@/components/ui/date-input-fr";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Rocket, ChevronRight, ChevronLeft, CheckCircle2, Loader2, SkipForward, Upload, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FORMES = ["SARL", "SAS", "SASU", "SA", "SNC", "EURL", "SCI", "Autre"];
const FONCTIONS = ["Gérant", "Président", "Co-Gérant", "Directeur Général", "Autre"];

export default function LegalOnboardingWizard({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    denomination: "", forme_juridique: "SARL", capital_social: "",
    siret: "", siren: "", siege_adresse: "", date_cloture_exercice: "12-31",
  });
  const [dirigeant, setDirigeant] = useState({
    civilite: "M.", prenom: "", nom: "", fonction: "Gérant", date_nomination: "",
  });
  const [associe, setAssocie] = useState({
    type: "physique", civilite: "M.", prenom: "", nom: "",
    raison_sociale: "", nombre_parts: 100, pourcentage: 100,
    adresse: "", code_postal: "", ville: "",
  });
  const [kbisFile, setKbisFile] = useState(null);
  const [kbisResult, setKbisResult] = useState(null);
  const [uploadingKbis, setUploadingKbis] = useState(false);

  useEffect(() => {
    if (!open) { return; }
    api.get("/legal/profile").then((r) => {
      const p = r.data || {};
      setProfile((cur) => ({ ...cur, ...Object.fromEntries(Object.keys(cur).filter((k) => p[k]).map((k) => [k, p[k]])) }));
      if (p.denomination) { setStep((s) => (s === 1 ? 2 : s)); }
    }).catch(() => {});
  }, [open]);

  const saveProfile = async () => {
    if (!profile.denomination) { toast.error("Dénomination requise"); return; }
    setSaving(true);
    try {
      const p = { ...profile };
      if (p.capital_social) p.capital_social = parseFloat(p.capital_social);
      else delete p.capital_social;
      await api.put("/legal/profile", p);
      toast.success("Profil enregistré");
      setStep(2);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  // Accepte "YYYY-MM-DD" (natif input type=date) OU "DD/MM/YYYY" (saisie manuelle
  // Safari qui n'a pas validé le picker). Retourne l'ISO ou "" si invalide.
  const normalizeDate = (v) => {
    if (!v) return "";
    const s = String(v).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const [_, d, mo, y] = m;
      return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    return "";
  };

  const saveDirigeant = async () => {
    const prenom = (dirigeant.prenom || "").trim();
    const nom = (dirigeant.nom || "").trim();
    const dateIso = normalizeDate(dirigeant.date_nomination);
    const missing = [];
    if (!prenom) missing.push("Prénom");
    if (!nom) missing.push("Nom");
    if (!dateIso) missing.push("Date de nomination (format JJ/MM/AAAA)");
    if (missing.length) {
      toast.error(`Champ(s) manquant(s) : ${missing.join(", ")}`);
      return;
    }
    const payload = { ...dirigeant, prenom, nom, date_nomination: dateIso };
    setSaving(true);
    try {
      await api.post("/legal/dirigeants", payload);
      toast.success("Dirigeant ajouté");
      // Pré-remplir associé physique avec les mêmes nom/prénom
      if (associe.type === "physique" && !associe.prenom) {
        setAssocie({ ...associe, prenom, nom, civilite: dirigeant.civilite });
      }
      setStep(3);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const saveAssocie = async () => {
    if (associe.type === "physique" && (!associe.prenom || !associe.nom)) {
      toast.error("Prénom et nom requis"); return;
    }
    if (associe.type === "morale" && !associe.raison_sociale) {
      toast.error("Raison sociale requise"); return;
    }
    setSaving(true);
    try {
      const payload = { ...associe };
      payload.nombre_parts = parseFloat(payload.nombre_parts) || 0;
      payload.pourcentage = parseFloat(payload.pourcentage) || 0;
      await api.post("/legal/associes", payload);
      toast.success("Associé ajouté");
      setStep(4);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const uploadKbis = async () => {
    if (!kbisFile) { toast.error("Sélectionnez le fichier KBIS"); return; }
    setUploadingKbis(true);
    try {
      // Extract with OCR typed to also index into context engine
      const fd = new FormData();
      fd.append("file", kbisFile);
      const r = await api.post("/legal/ocr/extract-typed?doc_type=kbis", fd);
      // Save as vault document
      const fd2 = new FormData();
      fd2.append("file", kbisFile);
      await api.post("/legal/documents/upload?category=kbis", fd2);
      setKbisResult(r.data.extracted);
      toast.success("KBIS analysé et archivé");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploadingKbis(false); }
  };

  const finish = () => {
    toast.success("🎉 Onboarding terminé !");
    onDone?.();
    onOpenChange(false);
  };

  const skip = async () => {
    try { await api.post("/legal/onboarding/dismiss"); }
    catch (e) { /* silent */ }
    onOpenChange(false);
  };

  const stepTitles = ["Profil société", "Dirigeant principal", "Associé principal", "Upload KBIS"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-purple-600" />
            Bienvenue dans Droit des sociétés
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-1 mb-4" data-testid="onboarding-progress">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full ${s < step ? "bg-emerald-500" : s === step ? "bg-purple-500" : "bg-slate-200"}`} />
              <p className={`text-[10px] mt-1 text-center ${s === step ? "font-semibold text-purple-700" : "text-slate-400"}`}>
                {stepTitles[s - 1]}
              </p>
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3" data-testid="onboarding-step-1">
            <p className="text-sm text-slate-600">Configurons le profil de votre société en 4 étapes rapides.</p>
            <div><Label>Dénomination sociale *</Label>
              <Input autoFocus value={profile.denomination} onChange={(e) => setProfile({ ...profile, denomination: e.target.value })} data-testid="onb-denomination" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Forme juridique *</Label>
                <Select value={profile.forme_juridique} onValueChange={(v) => setProfile({ ...profile, forme_juridique: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FORMES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Capital social (€)</Label>
                <Input type="number" value={profile.capital_social} onChange={(e) => setProfile({ ...profile, capital_social: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>SIRET</Label><Input value={profile.siret} onChange={(e) => setProfile({ ...profile, siret: e.target.value })} /></div>
              <div><Label>SIREN</Label><Input value={profile.siren} onChange={(e) => setProfile({ ...profile, siren: e.target.value })} /></div>
            </div>
            <div><Label>Siège social</Label><Textarea rows={2} value={profile.siege_adresse} onChange={(e) => setProfile({ ...profile, siege_adresse: e.target.value })} /></div>
            <div><Label>Clôture d'exercice (MM-DD)</Label><Input value={profile.date_cloture_exercice} onChange={(e) => setProfile({ ...profile, date_cloture_exercice: e.target.value })} /></div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="onboarding-step-2">
            <p className="text-sm text-slate-600">Qui dirige la société ? Ajoutez le mandataire principal (Gérant, Président…).</p>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Civilité</Label>
                <Select value={dirigeant.civilite} onValueChange={(v) => setDirigeant({ ...dirigeant, civilite: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="M.">M.</SelectItem><SelectItem value="Mme">Mme</SelectItem></SelectContent>
                </Select></div>
              <div><Label>Prénom *</Label><Input value={dirigeant.prenom} onChange={(e) => setDirigeant({ ...dirigeant, prenom: e.target.value })} data-testid="onb-dirigeant-prenom" /></div>
              <div><Label>Nom *</Label><Input value={dirigeant.nom} onChange={(e) => setDirigeant({ ...dirigeant, nom: e.target.value })} data-testid="onb-dirigeant-nom" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Fonction *</Label>
                <Select value={dirigeant.fonction} onValueChange={(v) => setDirigeant({ ...dirigeant, fonction: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FONCTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Date de nomination *</Label>
                <DateInputFR value={dirigeant.date_nomination} onChange={(e) => setDirigeant({ ...dirigeant, date_nomination: e.target.value })} data-testid="onb-dirigeant-date" /></div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="onboarding-step-3">
            <p className="text-sm text-slate-600">Ajoutez le premier associé. Vous pourrez en ajouter d'autres plus tard.</p>
            <div><Label>Type</Label>
              <Select value={associe.type} onValueChange={(v) => setAssocie({ ...associe, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="physique">Personne physique</SelectItem>
                  <SelectItem value="morale">Personne morale</SelectItem>
                </SelectContent>
              </Select></div>
            {associe.type === "physique" ? (
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Civilité</Label>
                  <Select value={associe.civilite} onValueChange={(v) => setAssocie({ ...associe, civilite: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="M.">M.</SelectItem><SelectItem value="Mme">Mme</SelectItem></SelectContent>
                  </Select></div>
                <div><Label>Prénom *</Label><Input value={associe.prenom} onChange={(e) => setAssocie({ ...associe, prenom: e.target.value })} /></div>
                <div><Label>Nom *</Label><Input value={associe.nom} onChange={(e) => setAssocie({ ...associe, nom: e.target.value })} /></div>
              </div>
            ) : (
              <div><Label>Raison sociale *</Label><Input value={associe.raison_sociale} onChange={(e) => setAssocie({ ...associe, raison_sociale: e.target.value })} /></div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nombre de parts *</Label><Input type="number" value={associe.nombre_parts} onChange={(e) => setAssocie({ ...associe, nombre_parts: e.target.value })} /></div>
              <div><Label>Pourcentage % *</Label><Input type="number" step="0.01" value={associe.pourcentage} onChange={(e) => setAssocie({ ...associe, pourcentage: e.target.value })} /></div>
            </div>
            <div><Label>Adresse postale (pour convocations)</Label>
              <Textarea rows={2} value={associe.adresse} onChange={(e) => setAssocie({ ...associe, adresse: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Code postal</Label><Input value={associe.code_postal} onChange={(e) => setAssocie({ ...associe, code_postal: e.target.value })} /></div>
              <div className="col-span-2"><Label>Ville</Label><Input value={associe.ville} onChange={(e) => setAssocie({ ...associe, ville: e.target.value })} /></div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3" data-testid="onboarding-step-4">
            <p className="text-sm text-slate-600">Uploadez votre KBIS. L'IA extraira les informations et enrichira automatiquement votre profil.</p>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <input id="onb-kbis-file" name="onb-kbis-file" type="file" accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => setKbisFile(e.target.files?.[0] || null)}
                      className="text-sm" data-testid="onb-kbis-file" />
              {kbisFile && <p className="mt-2 text-xs text-slate-600">{kbisFile.name}</p>}
            </div>
            {kbisResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm space-y-1" data-testid="onb-kbis-result">
                <p className="font-semibold text-emerald-700 flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />Données extraites
                </p>
                {kbisResult.denomination && <p>· Dénomination : <strong>{kbisResult.denomination}</strong></p>}
                {kbisResult.siret && <p>· SIRET : {kbisResult.siret}</p>}
                {kbisResult.capital_social && <p>· Capital : {kbisResult.capital_social} €</p>}
                {kbisResult.representant_legal && <p>· Représentant : {kbisResult.representant_legal}</p>}
              </div>
            )}
            {kbisFile && !kbisResult && (
              <Button onClick={uploadKbis} disabled={uploadingKbis} className="w-full" data-testid="onb-kbis-analyze">
                {uploadingKbis ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Analyser le KBIS avec l'IA
              </Button>
            )}
            <p className="text-xs text-slate-500 italic">
              Vous pouvez passer cette étape et uploader plus tard depuis le Coffre-fort.
            </p>
          </div>
        )}

        <div className="flex justify-between items-center pt-3 border-t">
          <Button variant="ghost" size="sm" onClick={skip} data-testid="onboarding-skip">
            <SkipForward className="w-3 h-3 mr-1" />Passer
          </Button>
          <div className="flex gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} disabled={saving}>
                <ChevronLeft className="w-4 h-4 mr-1" />Retour
              </Button>
            )}
            {step === 1 && (
              <Button onClick={saveProfile} disabled={saving} data-testid="onb-next-1">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Suivant<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {step === 2 && (
              <Button onClick={saveDirigeant} disabled={saving} data-testid="onb-next-2">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Suivant<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {step === 3 && (
              <Button onClick={saveAssocie} disabled={saving} data-testid="onb-next-3">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Suivant<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {step === 4 && (
              <Button onClick={finish} data-testid="onb-finish">
                <CheckCircle2 className="w-4 h-4 mr-2" />Terminer
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/** Hook auto-open : détecte si onboarding requis et retourne [open, setOpen, refresh]. */
export function useOnboardingStatus() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/legal/onboarding/status");
      setStatus(r.data);
      if (r.data.should_show) setOpen(true);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { refresh(); }, []);

  return { status, open, setOpen, refresh };
}
